import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listAdminApiService } from '@epics/adminManagment/api/listAdminApiService';
import { userStatusUpdateApiService } from '@epics/adminManagment/api/userStatusUpdateApiService';
import type { FilterOption, DateRange } from '@shared/components/FilterPopup';
import {
  ALLOWED_LIMITS,
  sanitizeDateString,
  sanitizeStatusString,
  isValidDateRange,
  validateStatusString,
  type ProcessedUrlParams,
  type FormattedAdmin,
} from './adminListValidation';

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useAdminList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);
  const workerRef = useRef<Worker | null>(null);

  // ── Synchronous init (main thread, runs once) ─────────────────────────────
  const initStartDate = sanitizeDateString(searchParams.get('startDate'));
  const initEndDate = sanitizeDateString(searchParams.get('endDate'));
  const initStatus = sanitizeStatusString(searchParams.get('status'));
  const initDateRangeValid = isValidDateRange(initStartDate, initEndDate);
  const initStatusValid = validateStatusString(initStatus ?? '');

  const [admins, setAdmins] = useState<FormattedAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => {
    const v = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(v) || v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = parseInt(searchParams.get('limit') ?? '10', 10);
    return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [search, setSearch] = useState(() => {
    const v = (searchParams.get('search') ?? '').trim();
    return v.length > 200 ? v.substring(0, 200) : v;
  });
  const [limitError, setLimitError] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef(search);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    if ((initStartDate || initEndDate) && !initDateRangeValid) {
      return {};
    }
    return { startDate: initStartDate, endDate: initEndDate };
  });

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() => {
    if (initStatus && !initStatusValid) {
      return [...DEFAULT_STATUS_OPTIONS];
    }
    if (initStatus) {
      return [
        {
          label: 'Aktywny',
          value: 'active',
          checked: initStatus.includes('active'),
        },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: initStatus.includes('inactive'),
        },
      ];
    }
    return [...DEFAULT_STATUS_OPTIONS];
  });

  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
    statusOptions: FilterOption[];
  }>(() => {
    if (
      (initStartDate || initEndDate || initStatus) &&
      (!initDateRangeValid || !initStatusValid)
    ) {
      return { dateRange: {}, statusOptions: [...DEFAULT_STATUS_OPTIONS] };
    }
    if (initStartDate || initEndDate || initStatus) {
      return {
        dateRange: { startDate: initStartDate, endDate: initEndDate },
        statusOptions: [
          {
            label: 'Aktywny',
            value: 'active',
            checked: initStatus?.includes('active') ?? false,
          },
          {
            label: 'Nieaktywny',
            value: 'inactive',
            checked: initStatus?.includes('inactive') ?? false,
          },
        ],
      };
    }
    return { dateRange: {}, statusOptions: [...DEFAULT_STATUS_OPTIONS] };
  });

  // ── Worker lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    const worker = new Worker(
      new URL('./adminList.worker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    worker.onmessage = (
      e: MessageEvent<{ type: string; payload: unknown }>
    ) => {
      const { type, payload } = e.data;

      // URL params validated → apply to React state
      if (type === 'PARAMS_PROCESSED') {
        const p = payload as ProcessedUrlParams;
        isSyncingFromUrl.current = true;

        setPage((prev) => (p.page !== prev ? p.page : prev));
        setLimit((prev) => (p.limit !== prev ? p.limit : prev));
        setSearch((prev) => (p.search !== prev.trim() ? p.search : prev));

        if (p.isDateRangeValid) {
          setDateRange((prev) => {
            if (prev.startDate === p.startDate && prev.endDate === p.endDate) {
              return prev;
            }
            return { startDate: p.startDate, endDate: p.endDate };
          });
        }

        if (p.isStatusValid) {
          const nextActive = p.status?.includes('active') ?? false;
          const nextInactive = p.status?.includes('inactive') ?? false;
          if (p.status) {
            setStatusOptions((prev) => {
              if (
                prev[0]?.checked === nextActive &&
                prev[1]?.checked === nextInactive
              ) {
                return prev;
              }
              return [
                { label: 'Aktywny', value: 'active', checked: nextActive },
                {
                  label: 'Nieaktywny',
                  value: 'inactive',
                  checked: nextInactive,
                },
              ];
            });
          } else {
            setStatusOptions((prev) =>
              !prev.some((o) => o.checked) ? prev : [...DEFAULT_STATUS_OPTIONS]
            );
          }
        }

        setAppliedFilters((prev) => {
          const nextDateRange = p.isDateRangeValid
            ? { startDate: p.startDate, endDate: p.endDate }
            : prev.dateRange;
          const nextStatus =
            p.isStatusValid && p.status
              ? [
                  {
                    label: 'Aktywny',
                    value: 'active',
                    checked: p.status.includes('active'),
                  },
                  {
                    label: 'Nieaktywny',
                    value: 'inactive',
                    checked: p.status.includes('inactive'),
                  },
                ]
              : [...DEFAULT_STATUS_OPTIONS];
          if (
            prev.dateRange?.startDate === nextDateRange?.startDate &&
            prev.dateRange?.endDate === nextDateRange?.endDate &&
            prev.statusOptions[0]?.checked === nextStatus[0]?.checked &&
            prev.statusOptions[1]?.checked === nextStatus[1]?.checked
          ) {
            return prev;
          }
          return { dateRange: nextDateRange, statusOptions: nextStatus };
        });
      }

      // Admin rows formatted → update admins state
      if (type === 'ADMINS_FORMATTED') {
        setAdmins(payload as FormattedAdmin[]);
        setLoading(false);
      }
    };

    return () => worker.terminate();
  }, []);

  // Reset sync flag after state settles
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, dateRange, statusOptions, appliedFilters]);

  // ── URL change → offload param processing to Worker ───────────────────────
  useEffect(() => {
    workerRef.current?.postMessage({
      type: 'PROCESS_PARAMS',
      payload: {
        page: searchParams.get('page'),
        search: searchParams.get('search'),
        limit: searchParams.get('limit'),
        startDate: searchParams.get('startDate'),
        endDate: searchParams.get('endDate'),
        status: searchParams.get('status'),
      },
    });
  }, [searchParams]);

  // ── State → URL sync (main thread) ───────────────────────────────────────
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }
    const params = new URLSearchParams();
    const searchChanged = prevSearchRef.current.trim() !== search.trim();
    if (searchChanged) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }
    params.set('limit', limit.toString());
    if (search) {
      params.set('search', search);
    }
    if (appliedFilters.dateRange?.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange?.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }
    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }
    if (
      params.toString() !==
      new URLSearchParams(searchParams.toString()).toString()
    ) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, search, appliedFilters, setSearchParams, searchParams]);

  // ── API fetch (main thread) — sends raw data to Worker for formatting ─────
  const fetchAdmins = useCallback(async () => {
    let didRedirect = false;
    try {
      setLoading(true);
      const selectedStatuses = appliedFilters.statusOptions
        .filter((o) => o.checked)
        .map((o) => o.value)
        .join(',');
      const trimmedSearch = debouncedSearch.trim();

      if (
        !isValidDateRange(
          appliedFilters.dateRange?.startDate,
          appliedFilters.dateRange?.endDate
        )
      ) {
        console.error('Invalid date range, clearing filters');
        setAppliedFilters({
          dateRange: {},
          statusOptions: [...DEFAULT_STATUS_OPTIONS],
        });
        setAdmins([]);
        setTotalCount(0);
        return;
      }

      if (selectedStatuses && !validateStatusString(selectedStatuses)) {
        console.error('Invalid status, clearing filters');
        setAppliedFilters({
          dateRange: appliedFilters.dateRange,
          statusOptions: [...DEFAULT_STATUS_OPTIONS],
        });
        setAdmins([]);
        setTotalCount(0);
        return;
      }

      const response = await listAdminApiService.listAdmins({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(selectedStatuses && { status: selectedStatuses }),
        ...(appliedFilters.dateRange.startDate && {
          startDate: appliedFilters.dateRange.startDate,
        }),
        ...(appliedFilters.dateRange.endDate && {
          endDate: appliedFilters.dateRange.endDate,
        }),
      });

      if (
        response.totalCount > 0 &&
        response.totalPages > 0 &&
        page > response.totalPages
      ) {
        didRedirect = true;
        setPage(response.totalPages);
        return;
      }

      setTotalCount(response.totalCount);
      setIsNoFilterFetch(
        !trimmedSearch &&
          !selectedStatuses &&
          !appliedFilters.dateRange.startDate &&
          !appliedFilters.dateRange.endDate
      );

      // Offload date formatting + initials to Worker — keeps render functions pure string reads
      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'FORMAT_ADMINS',
          payload: response.data,
        });
        // loading is set to false in the ADMINS_FORMATTED handler
      } else {
        setAdmins(response.data as unknown as FormattedAdmin[]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      setAdmins([]);
      setTotalCount(0);
      setLoading(false);
    } finally {
      if (!didRedirect && !workerRef.current) {
        setLoading(false);
      }
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  // Reset page when debounced search changes
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevDebouncedSearchRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedSearchRef.current.trim() !== debouncedSearch.trim()) {
      prevDebouncedSearchRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
      }
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // ── Event handlers (main thread) ──────────────────────────────────────────
  const handleStatusToggle = useCallback(
    async (adminId: string, newStatus: 'active' | 'inactive') => {
      try {
        setAdmins((prev) =>
          prev.map((admin) =>
            admin.id === adminId ? { ...admin, status: newStatus } : admin
          )
        );
      } catch (error) {
        console.error('Failed to update status:', error);
        fetchAdmins();
      }
    },
    [fetchAdmins]
  );

  const updateUserStatus = useCallback(
    async (
      userId: string,
      isActive: boolean
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const result = await userStatusUpdateApiService.updateUserStatus({
          userId,
          isActive,
        });
        if (result.success) {
          await fetchAdmins();
          return { success: true };
        }
        await fetchAdmins();
        return { success: false, ...(result.error && { error: result.error }) };
      } catch (error) {
        console.error('Failed to update user status:', error);
        await fetchAdmins();
        return {
          success: false,
          error: 'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.',
        };
      }
    },
    [fetchAdmins]
  );

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      if (!isSyncingFromUrl.current && search.trim() !== newSearch.trim()) {
        setPage(1);
      }
      if (!newSearch.trim() && search.trim()) {
        setLoading(true);
      }
      setSearch(newSearch);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    if (!isValidDateRange(dateRange.startDate, dateRange.endDate)) {
      console.error('Invalid date range, cannot apply');
      return;
    }
    const selectedStatuses = statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses && !validateStatusString(selectedStatuses)) {
      console.error('Invalid status, cannot apply');
      return;
    }
    setAppliedFilters({ dateRange, statusOptions });
    setPage(1);
  }, [dateRange, statusOptions]);

  const handleClearFilters = useCallback(() => {
    const hasFilters =
      appliedFilters.dateRange?.startDate ||
      appliedFilters.dateRange?.endDate ||
      appliedFilters.statusOptions.some((o) => o.checked);
    setDateRange({});
    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    if (hasFilters) {
      setAppliedFilters({
        dateRange: {},
        statusOptions: [...DEFAULT_STATUS_OPTIONS],
      });
      setPage(1);
    }
  }, [appliedFilters]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.dateRange?.startDate &&
    !appliedFilters.dateRange?.endDate &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const justClearedSearch =
    prevDebouncedSearchRef.current.trim() !== '' &&
    debouncedSearch.trim() === '' &&
    !appliedFilters.dateRange?.startDate &&
    !appliedFilters.dateRange?.endDate &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  return {
    admins,
    loading,
    page,
    setPage,
    limit,
    setLimit,
    totalCount,
    isNoFilterFetch,
    search,
    debouncedSearch,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: handleSearchChange,
    dateRange,
    setDateRange,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleStatusToggle,
    updateUserStatus,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
  };
};
